import type { BookOrderStatus } from './types';

/**
 * Block 1 · 1.4 · Book-slot derivation (Sebastian ruling 2026-09-09).
 *
 * Six side-image slots — one pair (left + right file) per screen:
 *   personen · geschichte · figuren · vorschau · bestellung · freigabe
 *
 * The mapping follows the pre-existing Schrittleiste step assignment
 * in `status-to-step.ts` so the side images move in lockstep with the
 * Bar-A / Bar-B mark. That is deliberate — one status, one screen,
 * one slot pair.
 *
 * Return type includes `null` for statuses where there is no
 * corresponding screen personality (cancelled, paused_billing,
 * reclamation_open, release-chain-error). Those render the neutral
 * BookStatusInfoScreen — no side image, no illusion of forward
 * progress.
 *
 * URL-path arg covers the two cases that live outside the envelope
 * lifecycle:
 *   - `/book`             — pre-order configurator (no orderId, no
 *                           envelope). Always `personen`.
 *   - `/book/:id/erfolg`  — post-Stripe success redirect. Envelope
 *                           may still be checkout_pending or paid
 *                           depending on webhook race; the URL is
 *                           the truthful signal. Always `bestellung`.
 *   - `/book/:id/bestellung` — Bestellübersicht two-phase flow. URL
 *                              wins over any transitional status.
 */

export type BookSlotName =
  | 'personen'
  | 'geschichte'
  | 'figuren'
  | 'vorschau'
  | 'bestellung'
  | 'freigabe';

export function deriveBookSlot(
  status: BookOrderStatus | null,
  urlPathname: string,
): BookSlotName | null {
  // URL-driven overrides — the configurator + the two /book/:orderId
  // sub-routes are not carried by envelope status alone.
  if (urlPathname === '/book') return 'personen';
  if (urlPathname.endsWith('/bestellung') || urlPathname.endsWith('/erfolg')) {
    return 'bestellung';
  }

  if (status === null) return 'personen';

  switch (status) {
    case 'draft_configuring':
    case 'light_gate_pending':
    case 'photo_rejected':
    // 2026-10-14 — per-figure block-derivation rejection. Mirrors
    // photo_rejected: customer lands on Personen with hints per
    // failed figure.
    case 'block_derivation_rejected':
      return 'personen';
    case 'summary_review':
      return 'geschichte';
    case 'anchor_pending':
    case 'anchor_review':
    case 'anchor_approved':
      return 'figuren';
    case 'story_generating':
    case 'text_editing':
    case 'preview_rendering':
    case 'preview_ready':
      return 'vorschau';
    case 'checkout_pending':
    case 'paid':
    case 'rendering_full':
    case 'qa_running':
    case 'qa_regen':
    case 'qa_human_queue':
      return 'bestellung';
    case 'customer_review':
    case 'print_approved':
    case 'prepress':
    case 'handoff_pod':
    case 'shipped':
    case 'delivered':
      return 'freigabe';
    case 'paused_billing':
    case 'reclamation_open':
    case 'cancelled':
    case 'book_release_chain_error':
    // Sebastian ruling 2026-09-10 (Paket 3) — image-refusal state.
    // No side image: same rationale as book_release_chain_error and
    // paused_billing — the state is a not-progressing stop point,
    // and the copy ("Das Bild konnte nicht gezeichnet werden") makes
    // clear the customer must change something rather than keep
    // waiting. A side image would suggest forward progress that is
    // not happening.
    case 'image_refused':
      return null;
  }
}
